import Loader from 'components/ui/LoadingIndicator';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageView } from 'components/ui/Feedback';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Redirect } from 'react-router-dom';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { useLogger } from '../../providers/logger/Logger';
import { useRoutesHistory } from '../../providers/RoutesHistoryProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { isValidMongoId } from '../../../utils/id-validation.utils';
import {
    MessageTypes,
    useNotification,
} from '../../providers/NotificationProvider';

const promotionContentSx: SxProps<Theme> = (theme) => ({
    maxWidth: 700,
    mx: 'auto',
    '& a': {
        color: 'link.default',
        textDecoration: 'none',
        '&:hover, &:focus': {
            textDecoration: 'none',
        },
        '& > u': {
            textDecoration: 'none',
        },
    },
    '& h1': {
        ...theme.typography.h4,
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h3,
        },
    },
    '& h2': {
        ...theme.typography.h5,
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h4,
        },
    },
    '& h3': {
        ...theme.typography.h6,
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h5,
        },
    },
    '& p, & li': {
        ...theme.typography.body1,
        lineHeight: theme.typography.body1.lineHeight,
    },
    '& .fr-img-caption .fr-img-wrap > span': theme.typography.caption,
    '& .fr-img-space-wrap': theme.typography.body1,
    '& img': {
        borderRadius: theme.shape.borderRadius,
        height: 'auto',
    },
});

export const PROMOTIONAL_CONTENT = gql`
    query promotionalContent($id: ID) {
        document: findPromotionalContentById(id: $id) {
            _id
            promotionPage {
                name
                html
                successTitle
                successMessage
            }
        }
    }
`;

export const NOTIFY_REGISTER_INTEREST_IN_PROMOTIONAL_CONTENT = gql`
    mutation notifyRegisteredInterestInPromotionalContent(
        $item: NotifyRegisteredInterestInPromotionalContentInput!
    ) {
        notifyRegisteredInterestInPromotionalContent(item: $item)
    }
`;

const PromotionPage: React.FC = () => {
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const isValidPromotionId = isValidMongoId(_id);
    const history = useHistory();
    const routesHistory = useRoutesHistory();
    const { printLog } = useLogger();

    const [actionButtonMounts, setActionButtonMounts] = useState<
        { label: string | null; placeholder: HTMLSpanElement }[]
    >([]);
    const { showMessage } = useNotification();

    const { loading, data } = useQuery(PROMOTIONAL_CONTENT, {
        skip: !isValidPromotionId,
        variables: { id: _id },
        fetchPolicy: 'network-only',
    });
    const [notifyRegisteredInterestInPromotionalContent] = useMutation(
        NOTIFY_REGISTER_INTEREST_IN_PROMOTIONAL_CONTENT
    );

    const promotionPage = data?.document?.promotionPage;

    const registerInterestHandler = useCallback(async () => {
        if (!_id) {
            return;
        }

        try {
            await notifyRegisteredInterestInPromotionalContent({
                variables: {
                    item: {
                        promotionalContentId: _id,
                    },
                },
            });
            printLog(
                LogLevel.INFO,
                LogAction.RegisteredInterest,
                'User registered interest',
                {
                    promotionalContentId: _id,
                    interest: promotionPage.name,
                }
            );
            showMessage(
                <>
                    <Typography variant="subtitle2">
                        {promotionPage.successTitle ?? 'We will be in touch'}
                    </Typography>
                    <Typography variant="body2">
                        {promotionPage.successMessage ??
                            `A member of the desk will contact you to set up your access to ${promotionPage.name}.`}
                    </Typography>
                </>,
                MessageTypes.success
            );
            if (routesHistory && routesHistory.history.length) {
                history.goBack();
            } else {
                history.push(baseUrls.forYou);
            }
        } catch (error) {
            printLog(
                LogLevel.ERROR,
                LogAction.RegisteredInterest,
                `Unable to register interest: ${
                    error instanceof Error ? error.message : String(error)
                }`,
                { promotionalContentId: _id, interest: promotionPage.name }
            );
            showMessage(
                'Unable to register your interest. Please try again.',
                MessageTypes.error
            );
        }
    }, [
        notifyRegisteredInterestInPromotionalContent,
        printLog,
        promotionPage,
        _id,
        showMessage,
        routesHistory,
        history,
    ]);

    useEffect(() => {
        const actionButtons = document
            .getElementById('promotion-html')
            ?.querySelectorAll<HTMLButtonElement>('.promotional-action-button');

        if (!actionButtons) {
            return;
        }

        const mountedButtons = Array.from(actionButtons).map((button) => {
            const placeholder = document.createElement('span');
            const label = button.textContent;
            button.replaceWith(placeholder);

            return { button, label, placeholder };
        });
        setActionButtonMounts(mountedButtons);

        return () =>
            mountedButtons.forEach(({ button, placeholder }) => {
                placeholder.replaceWith(button);
            });
    }, [promotionPage?.html, registerInterestHandler]);

    if (!isValidPromotionId) {
        return (
            <MessageView
                styleName="primary"
                size="large"
                title="Content not found"
                primaryAction={{
                    label: 'Return to Neon Insights',
                    onClick: () => history.push('/'),
                }}
            />
        );
    }

    if (loading) return <Loader />;

    if (!promotionPage) return <Redirect to={baseUrls.forYou} />;

    return (
        <Box
            sx={{
                pt: { xs: 4, md: 6 },
                pb: { xs: 4, md: 6 },
            }}
        >
            <Box sx={promotionContentSx}>
                <Box
                    id="promotion-html"
                    className="fr-view"
                    dangerouslySetInnerHTML={{
                        __html: promotionPage.html ?? '',
                    }}
                />
                <>
                    {actionButtonMounts.map(({ label, placeholder }, index) =>
                        createPortal(
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={registerInterestHandler}
                            >
                                {label}
                            </Button>,
                            placeholder,
                            index
                        )
                    )}
                </>
            </Box>
        </Box>
    );
};

export default PromotionPage;
